import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  type LinkComponentProps,
} from '@tanstack/react-router';
import { BrainIcon, StethoscopeIcon, type LucideIcon } from 'lucide-react';

import LogoHorizontal from '@assets/logo-horizontal.png';
import { Card } from '@components/ui/card';
import { ScrollArea } from '@components/ui/scroll-area';
import { pages } from '@constants/pages.constant';
import { cn } from '@lib/utils';

export const Route = createFileRoute('/_auth-layout')({
  beforeLoad: ({ context }) => {
    const isAuthenticated = !!context.auth.authorization.token;
    if (isAuthenticated) {
      throw redirect({
        to: '/treatments',
        search: {
          page: 1,
        },
      });
    }
  },
  component: AuthLayout,
});

function AuthLayout() {
  // const { navigate } = useRouter();
  const { pathname } = useLocation();

  // useEffect(() => {
  //   if (authorization.token) {
  //     navigate({
  //       to: pages.home,
  //       replace: true,
  //     });
  //   }
  // }, [authorization.token, navigate]);

  return (
    <div className="bg-primary relative">
      <div className="gradient-background absolute h-full min-h-dvh w-full" />
      <ScrollArea className="h-dvh bg-transparent">
        <main className="mx-auto flex min-h-dvh w-full flex-col items-center justify-center p-4">
          <div className="grid gap-x-4 md:grid-cols-2 lg:max-w-5xl lg:gap-x-12 xl:max-w-6xl">
            <div className="relative order-2 flex flex-col items-start justify-center md:order-0">
              <div className="mb-4">
                <Title />
                <Info
                  description="Ajude a construir a maior base de conteúdos médicos práticos do Brasil"
                  icon={BrainIcon}
                  title="Colabore"
                />

                <Info
                  description="Atualize conteúdos e ajude médicos a tomarem decisões mais seguras"
                  icon={StethoscopeIcon}
                  title="Salve vidas"
                />
              </div>
              <GPMedLogo />
            </div>

            <div className="order-1 mb-10 md:order-0 md:mb-0">
              <GPMedLogo isMobileVersion />
              <Title isMobileVersion />
              <Card.Container className="mx-auto w-full max-w-lg rounded-2xl bg-white/50 p-6 md:mx-0 lg:p-10">
                <Card.Header className="p-0">
                  {pathname !== pages.auth.forgotPassword && (
                    <div className="mx-auto flex h-12 items-center gap-x-1 rounded-lg bg-white/30 px-2">
                      <TabLink
                        label="Entrar"
                        pathname={pathname}
                        to="/sign-in"
                      />
                      <TabLink
                        label="Criar conta"
                        pathname={pathname}
                        to="/register"
                      />
                    </div>
                  )}

                  {pathname === pages.auth.forgotPassword && (
                    <>
                      <Card.Title className="text-primary mx-auto text-xl font-semibold">
                        Esqueceu sua senha?
                      </Card.Title>
                      <Card.Description className="text-primary text-center text-sm">
                        Informe seu e-mail e clique em "Recuperar senha". Logo
                        após será enviado o link para redefinir a senha no seu
                        WhatsApp.
                      </Card.Description>
                    </>
                  )}
                </Card.Header>
                <Outlet />
              </Card.Container>
            </div>
          </div>
        </main>
      </ScrollArea>
    </div>
  );
}

type TitleProps = {
  isMobileVersion?: boolean;
};
function Title({ isMobileVersion = false }: TitleProps) {
  return (
    <h1
      className={cn('text-white', {
        'mt-2 mb-12 text-center text-2xl font-bold md:hidden': isMobileVersion,
        'mx-auto mb-8 hidden text-center text-3xl font-extrabold md:block':
          !isMobileVersion,
      })}>
      O seu conhecimento salva vidas
    </h1>
  );
}

type InfoProps = {
  description: string;
  icon: LucideIcon;
  title: string;
};

function Info({ description, icon: Icon, title }: InfoProps) {
  return (
    <div className="flex items-center px-3 py-2">
      <Icon className="mr-6 size-18 text-white md:size-24" strokeWidth="1" />
      <div>
        <h3 className="text-lg font-semibold text-white md:text-xl">{title}</h3>
        <p className="text-sm text-white">{description}</p>
      </div>
    </div>
  );
}

type GPMedLogoProps = {
  isMobileVersion?: boolean;
};

function GPMedLogo({ isMobileVersion = false }: GPMedLogoProps) {
  return (
    <img
      alt="Logo GPMED"
      className={cn('relative mx-auto mt-10 w-auto md:mt-0 md:mb-4 md:h-14', {
        'h-12 md:hidden': isMobileVersion,
        'h-8': !isMobileVersion,
      })}
      src={LogoHorizontal}
    />
  );
}

type TabLinkProps = Pick<LinkComponentProps, 'to'> & {
  label: string;
  pathname: string;
};

function TabLink({ label, pathname, to }: TabLinkProps) {
  return (
    <Link
      className={cn(
        'text-primary/70 flex h-8 w-30 items-center justify-center rounded-md text-sm transition-all duration-300',
        {
          'bg-primary text-white': pathname === to,
          'hover:bg-primary hover:text-white': pathname !== to,
        },
      )}
      preload={false}
      to={to}>
      {label}
    </Link>
  );
}
